/**
 * Onglet Test de Récompenses pour l'administration
 * @module admin/tabs/RewardsTestTab
 */

import React, { useMemo } from 'react';
import { useRewardsTest } from '../hooks/useRewardsTest';
import AdminResult from '../components/AdminResult';

/**
 * Composant onglet Test de Récompenses
 * @returns {JSX.Element}
 */
const RewardsTestTab = () => {
  const {
    rewardTypes,
    rewards,
    selectedRewardTitle,
    selectedRewardId,
    testOptions,
    isLoading,
    isLoadingRewards,
    result,
    error,
    history,
    fetchRewards,
    handleRewardTitleChange,
    handleRewardSelect,
    handleOptionChange,
    handleSimulateReward,
    replayTest,
    deleteHistoryEntry,
    clearHistory,
  } = useRewardsTest();

  // Créer une liste de suggestions basée sur les types de récompenses
  const rewardSuggestions = useMemo(() => {
    const suggestions = new Set();
    rewardTypes.forEach((type) => {
      type.keywords.forEach((keyword) => {
        suggestions.add(keyword);
      });
    });
    return Array.from(suggestions).sort();
  }, [rewardTypes]);

  return (
    <>
      <section className="admin-section">
        <h2>Tester une récompense</h2>
        <p className="admin-info">
          Simulez une rédemption de récompense Twitch sans passer par le chat.
          Aucun message ne sera envoyé dans le chat (simulation admin).
        </p>

        <div className="admin-form">
          <div className="admin-field">
            <label htmlFor="rewardSelect">Sélectionner une récompense (optionnel)</label>
            <select
              id="rewardSelect"
              value={selectedRewardId}
              onChange={(e) => handleRewardSelect(e.target.value)}
              disabled={isLoadingRewards}
            >
              <option value="">Choisir une récompense depuis Twitch...</option>
              {rewards.map((reward) => (
                <option key={reward.id} value={reward.id}>
                  {reward.title} ({reward.cost} points)
                  {reward.isPaused && ' [En pause]'}
                  {!reward.isEnabled && ' [Désactivée]'}
                </option>
              ))}
            </select>
            {isLoadingRewards && (
              <small className="admin-hint">Chargement des récompenses...</small>
            )}
            {!isLoadingRewards && rewards.length === 0 && (
              <small className="admin-hint" style={{ color: 'var(--admin-warning-text, #856404)' }}>
                Aucune récompense trouvée. Vérifiez votre connexion Twitch.
              </small>
            )}
            {rewards.length > 0 && (
              <button
                type="button"
                className="admin-button admin-button-secondary"
                onClick={() => fetchRewards()}
                style={{ marginTop: '0.5rem', fontSize: '0.9em', padding: '0.4rem 0.8rem' }}
              >
                🔄 Actualiser la liste
              </button>
            )}
          </div>

          <div className="admin-field">
            <label htmlFor="rewardTitle">Titre de la récompense</label>
            <input
              id="rewardTitle"
              type="text"
              value={selectedRewardTitle}
              onChange={(e) => handleRewardTitleChange(e.target.value)}
              placeholder="Ex: Skip musique, TO, Ajouter une musique"
              list="rewardSuggestions"
            />
            <datalist id="rewardSuggestions">
              {rewardSuggestions.map((suggestion) => (
                <option key={suggestion} value={suggestion} />
              ))}
            </datalist>
            <small className="admin-hint">
              Vous pouvez saisir un titre manuellement ou sélectionner une récompense ci-dessus.
              Suggestions: {rewardSuggestions.slice(0, 5).join(', ')}
              {rewardSuggestions.length > 5 && '...'}
            </small>
          </div>

          {rewardTypes.length > 0 && (
            <div className="admin-info-box">
              <p>
                <strong>Types de récompenses supportés:</strong>
              </p>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                {rewardTypes.map((type) => (
                  <li key={type.type}>
                    <strong>{type.name}:</strong> {type.keywords.join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="admin-field-group">
            <h3>Options de simulation</h3>

            <div className="admin-field">
              <label htmlFor="userInput">Input utilisateur (optionnel)</label>
              <input
                id="userInput"
                type="text"
                value={testOptions.userInput}
                onChange={(e) =>
                  handleOptionChange('userInput', e.target.value)
                }
                placeholder="Ex: Song Name by Artist (pour ajout musique) ou targetuser (pour TO)"
              />
              <small className="admin-hint">
                Requis pour certaines récompenses (ex: TO nécessite un pseudo,
                Ajouter une musique nécessite un nom de chanson)
              </small>
            </div>

            <div className="admin-field">
              <label htmlFor="testUsername">Nom d'utilisateur</label>
              <input
                id="testUsername"
                type="text"
                value={testOptions.username}
                onChange={(e) => handleOptionChange('username', e.target.value)}
                placeholder="test_user"
              />
            </div>

            <div className="admin-field">
              <label htmlFor="testDisplayName">Nom d'affichage</label>
              <input
                id="testDisplayName"
                type="text"
                value={testOptions.displayName}
                onChange={(e) =>
                  handleOptionChange('displayName', e.target.value)
                }
                placeholder="Test User"
              />
            </div>

            <div className="admin-field">
              <label htmlFor="testUserId">ID utilisateur (optionnel)</label>
              <input
                id="testUserId"
                type="text"
                value={testOptions.userId}
                onChange={(e) => handleOptionChange('userId', e.target.value)}
                placeholder="123456789"
              />
            </div>

            <div className="admin-field">
              <label htmlFor="testRewardId">ID récompense (optionnel)</label>
              <input
                id="testRewardId"
                type="text"
                value={testOptions.rewardId}
                onChange={(e) => handleOptionChange('rewardId', e.target.value)}
                placeholder="custom-reward-id"
              />
            </div>
          </div>

          <button
            className="admin-button admin-button-primary"
            onClick={handleSimulateReward}
            disabled={isLoading || !selectedRewardTitle}
          >
            {isLoading ? 'Simulation...' : 'Simuler la récompense'}
          </button>
        </div>

        {result && (
          <AdminResult
            isSuccess={result.success}
            title={result.success ? 'Résultat' : 'Erreur'}
          >
            {result.message && (
              <p>
                <strong>Message:</strong> {result.message}
              </p>
            )}

            {result.rewardType && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'var(--admin-bg-secondary)', borderRadius: '4px' }}>
                <p style={{ margin: 0 }}>
                  <strong>Type détecté:</strong> {result.rewardTypeName || result.rewardType}
                  {result.rewardType && (
                    <span style={{ color: 'var(--admin-success)', marginLeft: '0.5rem' }}>✅</span>
                  )}
                </p>
                {result.processing && (
                  <p style={{ marginTop: '0.5rem', marginBottom: 0, fontSize: '0.9em', color: 'var(--admin-text-secondary)' }}>
                    Traitement: {result.processing.timeMs}ms
                  </p>
                )}
              </div>
            )}

            {result.warning && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'var(--admin-warning-bg, #fff3cd)', borderRadius: '4px', border: '1px solid var(--admin-warning-border, #ffc107)' }}>
                <p style={{ margin: 0, color: 'var(--admin-warning-text, #856404)' }}>
                  <strong>⚠️ Attention:</strong> {result.warning}
                </p>
                {result.debug && (
                  <div style={{ marginTop: '1rem' }}>
                    <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
                      <strong>Titre testé:</strong> "{result.debug.title}"
                    </p>
                    {result.debug.suggestion && (
                      <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
                        <strong>Suggestion:</strong> {result.debug.suggestion}
                      </p>
                    )}
                    {result.debug.testedKeywords && (
                      <details style={{ marginTop: '0.5rem' }}>
                        <summary style={{ cursor: 'pointer', fontSize: '0.9em' }}>
                          Voir les mots-clés testés
                        </summary>
                        <div className="admin-output" style={{ marginTop: '0.5rem', fontSize: '0.85em' }}>
                          <pre>{JSON.stringify(result.debug.testedKeywords, null, 2)}</pre>
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}

            {result.event && (
              <div className="admin-output" style={{ marginTop: '1rem' }}>
                <strong>Détails de l'événement simulé:</strong>
                <pre>{JSON.stringify(result.event, null, 2)}</pre>
              </div>
            )}

            {result.processing && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'var(--admin-bg-secondary)', borderRadius: '4px' }}>
                <p style={{ margin: 0 }}>
                  <strong>Informations de traitement:</strong>
                </p>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li>Temps de traitement: {result.processing.timeMs}ms</li>
                  <li>Type détecté: {result.processing.detected ? '✅ Oui' : '❌ Non'}</li>
                  <li>Type reconnu: {result.processing.recognized ? '✅ Oui' : '❌ Non'}</li>
                </ul>
              </div>
            )}

            {result.error && (
              <div className="admin-warnings" style={{ marginTop: '1rem' }}>
                <strong>Erreur:</strong>
                <pre>{result.error}</pre>
              </div>
            )}

            {result.stack && process.env.NODE_ENV === 'development' && (
              <div className="admin-warnings" style={{ marginTop: '1rem' }}>
                <strong>Stack trace:</strong>
                <pre>{result.stack}</pre>
              </div>
            )}
          </AdminResult>
        )}

        {error && (
          <AdminResult isSuccess={false} title="Erreur" message={error} />
        )}
      </section>

      <section className="admin-section">
        <div className="admin-section-header">
          <h2>Historique des tests</h2>
          {history.length > 0 && (
            <button
              className="admin-button admin-button-secondary"
              onClick={clearHistory}
            >
              Effacer l'historique
            </button>
          )}
        </div>

        {history.length === 0 ? (
          <p className="admin-info">Aucun test dans l'historique.</p>
        ) : (
          <div className="admin-history">
            {history.map((entry) => (
              <div key={entry.id} className="admin-history-entry">
                <div className="admin-history-header">
                  <span className="admin-history-event">
                    {entry.rewardTitle}
                    {entry.options.userInput &&
                      ` - "${entry.options.userInput}"`}
                  </span>
                  <span className="admin-history-time">
                    {new Date(entry.timestamp).toLocaleString('fr-FR')}
                  </span>
                </div>
                <div className="admin-history-options">
                  <p>
                    <strong>Utilisateur:</strong> {entry.options.username}
                    {entry.options.displayName &&
                      ` (${entry.options.displayName})`}
                  </p>
                  {entry.result && entry.result.rewardType && (
                    <p>
                      <strong>Type détecté:</strong> {entry.result.rewardTypeName || entry.result.rewardType}
                      {entry.result.rewardType && ' ✅'}
                    </p>
                  )}
                  {entry.result && entry.result.message && (
                    <p>
                      <strong>Résultat:</strong> {entry.result.message}
                    </p>
                  )}
                  {entry.result && entry.result.success !== undefined && (
                    <p>
                      <strong>Succès:</strong>{' '}
                      {entry.result.success ? '✅ Oui' : '❌ Non'}
                    </p>
                  )}
                  {entry.result && entry.result.warning && (
                    <p style={{ color: 'var(--admin-warning-text, #856404)' }}>
                      <strong>⚠️:</strong> {entry.result.warning}
                    </p>
                  )}
                  {entry.result && entry.result.processing && (
                    <p style={{ fontSize: '0.9em', color: 'var(--admin-text-secondary)' }}>
                      <strong>Temps:</strong> {entry.result.processing.timeMs}ms
                    </p>
                  )}
                </div>
                <div className="admin-history-actions">
                  <button
                    className="admin-button admin-button-small"
                    onClick={() => replayTest(entry)}
                  >
                    Rejouer
                  </button>
                  <button
                    className="admin-button admin-button-small admin-button-danger"
                    onClick={() => deleteHistoryEntry(entry.id)}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default RewardsTestTab;
